import {
  claimMissingUserUiPreferences,
  fetchUserUiPreferences,
  patchUserUiPreferences,
} from "./userUiPreferencesApi.js";
import {
  mirrorConfirmedUserUiPreferencesToLegacy,
  readLegacyUserUiPreferencesPatch,
} from "../utils/legacyUserUiPreferences.js";
import {
  assertValidUserUiPreferencesPatch,
  assertValidUserUiPreferencesPayload,
  DEFAULT_USER_UI_PREFERENCES,
  readUserUiPreferencesCache,
  resolveUserUiPreferencesUserId,
  writeUserUiPreferencesCache,
} from "../utils/userUiPreferences.js";

export const USER_UI_PREFERENCES_SAVE_DEBOUNCE_MS = 500;
const OPEN_PAGE_DATE_KEYS = ["last_dispatch_date", "last_consolidation_from_date", "last_consolidation_to_date"];

function clonePreferences(preferences) {
  return {
    dispatch_color_intensity_percent: preferences.dispatch_color_intensity_percent,
    protect_after_reassignment: preferences.protect_after_reassignment,
    dispatch_reassignment_mode: preferences.dispatch_reassignment_mode,
    route_map_group_ranges_visible: preferences.route_map_group_ranges_visible,
    optimization_animation_enabled: preferences.optimization_animation_enabled,
    last_dispatch_date: preferences.last_dispatch_date,
    last_consolidation_from_date: preferences.last_consolidation_from_date,
    last_consolidation_to_date: preferences.last_consolidation_to_date,
    consolidation_hide_arrival: preferences.consolidation_hide_arrival,
    consolidation_show_planned: preferences.consolidation_show_planned,
    consolidation_hide_timing: preferences.consolidation_hide_timing,
    consolidation_show_order: preferences.consolidation_show_order,
    consolidation_show_billed: preferences.consolidation_show_billed,
    consolidation_show_hours_summary: preferences.consolidation_show_hours_summary,
    sidebar_collapsed: preferences.sidebar_collapsed,
    dismissed_product_update_ids: [...preferences.dismissed_product_update_ids],
  };
}

function preferenceValuesEqual(left, right) {
  if (Array.isArray(left) || Array.isArray(right)) {
    return Array.isArray(left)
      && Array.isArray(right)
      && left.length === right.length
      && left.every((value, index) => value === right[index]);
  }
  return left === right;
}

function hasOwnKeys(value) {
  return Object.keys(value).length > 0;
}

function safelyNotifyError(callback, error, meta) {
  if (typeof callback !== "function") {
    return;
  }

  try {
    callback(error, meta);
  } catch {}
}

export function createInitialUserUiPreferencesState(userId, {
  readCache = readUserUiPreferencesCache,
  readLegacy = readLegacyUserUiPreferencesPatch,
  targetWindow,
} = {}) {
  const resolvedUserId = resolveUserUiPreferencesUserId(userId);
  const cachedEntry = resolvedUserId
    ? readCache(resolvedUserId, { targetWindow })
    : null;
  const legacyPatch = resolvedUserId
    ? readLegacy(resolvedUserId, { targetWindow })
    : {};
  const preferences = cachedEntry?.preferences
    ? assertValidUserUiPreferencesPayload(cachedEntry.preferences)
    : clonePreferences({
      ...DEFAULT_USER_UI_PREFERENCES,
      ...legacyPatch,
    });

  return {
    confirmedPreferences: clonePreferences(preferences),
    error: null,
    errorMessage: "",
    hasPendingChanges: false,
    isLoading: Boolean(resolvedUserId),
    isReady: true,
    isSaving: false,
    preferences: clonePreferences(preferences),
    source: cachedEntry ? "cache" : hasOwnKeys(legacyPatch) ? "legacy" : "default",
    status: resolvedUserId ? "loading" : "idle",
    userId: resolvedUserId,
  };
}

export function createUserUiPreferencesController({
  claimMissingPreferences = claimMissingUserUiPreferences,
  clearTimeoutImpl = globalThis.clearTimeout?.bind(globalThis),
  debounceMs = USER_UI_PREFERENCES_SAVE_DEBOUNCE_MS,
  keepOpenPageDatesLocal = false,
  loadPreferences = fetchUserUiPreferences,
  mirrorConfirmed = mirrorConfirmedUserUiPreferencesToLegacy,
  onError,
  readCache = readUserUiPreferencesCache,
  readLegacy = readLegacyUserUiPreferencesPatch,
  savePreferences = patchUserUiPreferences,
  setTimeoutImpl = globalThis.setTimeout?.bind(globalThis),
  targetWindow,
  userId,
  writeCache = writeUserUiPreferencesCache,
} = {}) {
  const resolvedUserId = resolveUserUiPreferencesUserId(userId);
  const initialState = createInitialUserUiPreferencesState(resolvedUserId, {
    readCache,
    readLegacy,
    targetWindow,
  });
  let confirmedPreferences = clonePreferences(initialState.confirmedPreferences);
  let error = null;
  let inFlightPatch = {};
  let isDisposed = false;
  let isLoading = false;
  let loadAbortController = null;
  let loadSequence = 0;
  let localPatch = {};
  let pendingPatch = {};
  let saveAbortController = null;
  let savePromise = null;
  let saveRevision = 0;
  let saveTimer = null;
  let source = initialState.source;
  let snapshot = initialState;
  const listeners = new Set();

  const resolveCurrentPreferences = () => ({
    ...confirmedPreferences,
    ...inFlightPatch,
    ...pendingPatch,
    ...localPatch,
  });

  const rebuildSnapshot = () => {
    const hasPendingChanges = hasOwnKeys(inFlightPatch) || hasOwnKeys(pendingPatch);
    const isSaving = Boolean(savePromise) || hasOwnKeys(pendingPatch);
    const preferences = resolveCurrentPreferences();
    const status = !resolvedUserId
      ? "idle"
      : isSaving
        ? "saving"
        : isLoading
          ? "loading"
          : error
            ? "error"
            : "ready";

    snapshot = Object.freeze({
      confirmedPreferences: Object.freeze(clonePreferences(confirmedPreferences)),
      error,
      errorMessage: error instanceof Error ? error.message : "",
      hasPendingChanges,
      isLoading,
      isReady: true,
      isSaving,
      preferences: Object.freeze(clonePreferences(preferences)),
      source: hasPendingChanges ? "optimistic" : source,
      status,
      userId: resolvedUserId,
    });
  };

  const publish = () => {
    rebuildSnapshot();
    for (const listener of listeners) {
      listener(snapshot);
    }
  };

  const clearSaveTimer = () => {
    if (saveTimer == null) {
      return;
    }

    if (typeof clearTimeoutImpl === "function") {
      clearTimeoutImpl(saveTimer);
    }
    saveTimer = null;
  };

  const scheduleSave = () => {
    clearSaveTimer();
    if (
      isDisposed
      || !resolvedUserId
      || !hasOwnKeys(pendingPatch)
      || typeof setTimeoutImpl !== "function"
    ) {
      return;
    }

    saveTimer = setTimeoutImpl(() => {
      saveTimer = null;
      void flush().catch(() => {});
    }, Math.max(0, Number.isFinite(debounceMs) ? debounceMs : USER_UI_PREFERENCES_SAVE_DEBOUNCE_MS));
  };

  const persistConfirmedPreferences = () => {
    writeCache(resolvedUserId, confirmedPreferences, { targetWindow });
    mirrorConfirmed(resolvedUserId, confirmedPreferences, { targetWindow });
  };

  async function load() {
    if (isDisposed || !resolvedUserId) {
      return snapshot;
    }

    loadAbortController?.abort();
    const requestAbortController = new AbortController();
    loadAbortController = requestAbortController;
    const requestSequence = ++loadSequence;
    const saveRevisionAtStart = saveRevision;
    isLoading = true;
    error = null;
    publish();

    try {
      const legacyPatch = source === "cache" || source === "server"
        ? {}
        : readLegacy(resolvedUserId, { targetWindow });
      const payload = hasOwnKeys(legacyPatch)
        ? await claimMissingPreferences(legacyPatch, { signal: requestAbortController.signal })
        : await loadPreferences({ signal: requestAbortController.signal });
      const serverPreferences = assertValidUserUiPreferencesPayload(payload);
      if (
        isDisposed
        || requestAbortController.signal.aborted
        || requestSequence !== loadSequence
      ) {
        return snapshot;
      }

      // A PATCH completed after this GET started. Its response is a more recent
      // server truth than the potentially stale GET payload.
      if (saveRevision === saveRevisionAtStart) {
        confirmedPreferences = serverPreferences;
        source = "server";
        persistConfirmedPreferences();
      }
      error = null;
    } catch (nextError) {
      if (
        isDisposed
        || requestAbortController.signal.aborted
        || requestSequence !== loadSequence
      ) {
        return snapshot;
      }

      if (saveRevision !== saveRevisionAtStart) {
        error = null;
        return snapshot;
      }

      error = nextError instanceof Error
        ? nextError
        : new Error("Impossible de charger les préférences d’interface.");
      safelyNotifyError(onError, error, {
        operation: "load",
        preferences: clonePreferences(resolveCurrentPreferences()),
        userId: resolvedUserId,
      });
      throw error;
    } finally {
      if (
        !isDisposed
        && requestSequence === loadSequence
        && loadAbortController === requestAbortController
      ) {
        loadAbortController = null;
        isLoading = false;
        publish();
      }
    }
    return snapshot;
  }

  function compactPendingPatch() {
    for (const [key, value] of Object.entries(pendingPatch)) {
      const effectiveWithoutPending = Object.hasOwn(inFlightPatch, key)
        ? inFlightPatch[key]
        : confirmedPreferences[key];
      if (preferenceValuesEqual(value, effectiveWithoutPending)) {
        delete pendingPatch[key];
      }
    }
  }

  function updatePreferences(patch, { immediate = false } = {}) {
    if (isDisposed) {
      throw new Error("Le gestionnaire des préférences d’interface est fermé.");
    }
    if (!resolvedUserId) {
      throw new Error("Aucun utilisateur authentifié ne peut enregistrer cette préférence.");
    }

    const normalizedPatch = assertValidUserUiPreferencesPatch(patch);
    const currentPreferences = resolveCurrentPreferences();
    const hasChange = Object.entries(normalizedPatch).some(
      ([key, value]) => !preferenceValuesEqual(currentPreferences[key], value),
    );
    if (!hasChange) {
      return immediate ? Promise.resolve(snapshot) : snapshot;
    }

    const savedPatch = { ...normalizedPatch };
    // Admin qui règle pour un autre : les journées qu'il ouvre restent dans son onglet, jamais chez l'utilisateur.
    for (const key of keepOpenPageDatesLocal ? OPEN_PAGE_DATE_KEYS : []) {
      if (!Object.hasOwn(savedPatch, key)) continue;
      localPatch = { ...localPatch, [key]: savedPatch[key] };
      delete savedPatch[key];
    }
    pendingPatch = {
      ...pendingPatch,
      ...savedPatch,
    };
    compactPendingPatch();
    error = null;

    if (hasOwnKeys(pendingPatch)) {
      scheduleSave();
    } else {
      clearSaveTimer();
    }
    publish();

    return immediate ? flush() : snapshot;
  }

  async function flush() {
    clearSaveTimer();
    if (isDisposed || !resolvedUserId) {
      return snapshot;
    }

    if (savePromise) {
      await savePromise.catch(() => undefined);
      return hasOwnKeys(pendingPatch) ? flush() : snapshot;
    }

    if (!hasOwnKeys(pendingPatch)) {
      return snapshot;
    }

    const patch = pendingPatch;
    pendingPatch = {};
    inFlightPatch = patch;
    const requestAbortController = new AbortController();
    saveAbortController = requestAbortController;
    publish();

    const currentSavePromise = Promise.resolve().then(async () => {
      try {
        const payload = await savePreferences(patch, {
          signal: requestAbortController.signal,
        });
        const serverPreferences = { ...assertValidUserUiPreferencesPayload(payload) };
        if (isDisposed || requestAbortController.signal.aborted) {
          return snapshot;
        }

        // Une réponse complète à un choix d'affichage ne change pas la journée ouverte.
        for (const key of OPEN_PAGE_DATE_KEYS) {
          if (!Object.hasOwn(patch, key)) serverPreferences[key] = confirmedPreferences[key];
        }
        confirmedPreferences = serverPreferences;
        saveRevision += 1;
        source = "server";
        error = null;
        persistConfirmedPreferences();
      } catch (nextError) {
        if (isDisposed || requestAbortController.signal.aborted) {
          return snapshot;
        }

        error = nextError instanceof Error
          ? nextError
          : new Error("Impossible d’enregistrer les préférences d’interface.");
        safelyNotifyError(onError, error, {
          attemptedPatch: { ...patch },
          operation: "save",
          rolledBackPreferences: clonePreferences({
            ...confirmedPreferences,
            ...pendingPatch,
          }),
          userId: resolvedUserId,
        });
        throw error;
      } finally {
        if (saveAbortController === requestAbortController) {
          saveAbortController = null;
        }
        inFlightPatch = {};
        if (savePromise === currentSavePromise) {
          savePromise = null;
        }
        if (!isDisposed) {
          if (hasOwnKeys(pendingPatch)) {
            scheduleSave();
          }
          publish();
        }
      }
      return snapshot;
    });

    savePromise = currentSavePromise;
    publish();
    return currentSavePromise;
  }

  async function reload() {
    if (hasOwnKeys(pendingPatch) || savePromise) {
      await flush().catch(() => undefined);
    }
    return load();
  }

  function acceptRemotePreferences(payload) {
    if (isDisposed || snapshot.status === "loading" || isLoading || savePromise || hasOwnKeys(pendingPatch) || hasOwnKeys(inFlightPatch)) return false;
    const preferences = { ...assertValidUserUiPreferencesPayload(payload) };
    // Les souvenirs de dates ne doivent pas déplacer une saisie métier vers une autre journée.
    for (const key of OPEN_PAGE_DATE_KEYS) {
      preferences[key] = confirmedPreferences[key];
    }
    if (JSON.stringify(preferences) === JSON.stringify(confirmedPreferences) && !error) return true;
    confirmedPreferences = clonePreferences(preferences);
    error = null;
    saveRevision++;
    source = "server";
    persistConfirmedPreferences();
    publish();
    return true;
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      return () => {};
    }

    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  function dispose() {
    if (isDisposed) {
      return;
    }

    isDisposed = true;
    loadSequence += 1;
    clearSaveTimer();
    loadAbortController?.abort();
    saveAbortController?.abort();
    loadAbortController = null;
    saveAbortController = null;
    listeners.clear();
  }

  rebuildSnapshot();

  return Object.freeze({
    acceptRemotePreferences,
    dispose,
    flush,
    getSnapshot() {
      return snapshot;
    },
    load,
    reload,
    subscribe,
    updatePreferences,
  });
}
